import { useEffect, useRef, useState } from 'react';
import { Check, Shield, Layers, DollarSign } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_pay-next-gen/artifacts/vywq6zhw_BLACKLOGO.png";
const SPLASH_URL = "https://customer-assets.emergentagent.com/job_pay-next-gen/artifacts/ikoslndy_Splash%20ellipse.png";

const differentiators = [
  {
    icon: Layers,
    title: 'One Integration. Every Rail.',
    description: 'NexGenPay replaces your entire multi-vendor payments stack — Payfinia, Stablecore, and beyond — with a single integration. FedNow, RTP, Stablecoin, and Tokenized Deposits through one platform, under your brand.',
  },
  {
    icon: Shield,
    title: 'Examiner-Ready by Construction',
    description: 'OFAC screening, BSA/AML enforcement, Travel Rule compliance, and GENIUS Act-ready architecture built in from day one. Every decision is audit-logged. Every exam, you\'re ready.',
  },
  {
    icon: DollarSign,
    title: 'Operational Sovereignty',
    description: 'Bank Admin Console gives your team full control over every parameter — rails, limits, compliance thresholds, customer tiers. Your brand. Your data. Your revenue. Your rules.',
  },
];

export default function SolutionSection() {
  const sectionRef = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setRevealed(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="solution"
      data-testid="solution-section"
      ref={sectionRef}
      className="relative section-padding"
      style={{
        scrollMarginTop: '80px',
        background: 'linear-gradient(180deg, #000 0%, #050505 50%, #000 100%)',
      }}
    >
      {/* Green glow top */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[200px] opacity-10"
        style={{ background: 'radial-gradient(ellipse, #00FF00, transparent 70%)' }} />

      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

          {/* Left: Content */}
          <div
            style={{
              opacity: revealed ? 1 : 0,
              transform: revealed ? 'translateX(0)' : 'translateX(-40px)',
              transition: 'all 0.8s ease',
            }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="green-line" />
              <span className="text-[#00FF00] font-mono text-xs uppercase tracking-widest">The Solution</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-black uppercase text-white mb-4 leading-tight"
              style={{ fontFamily: 'Unbounded, sans-serif' }}>
              The Only Full-Stack
              <br />
              <span className="text-[#00FF00]">Four-Rail Wallet</span>
            </h2>
            <p className="text-[#A3A3A3] text-base leading-relaxed mb-10"
              style={{ fontFamily: 'Manrope, sans-serif' }}>
              NexGenPay is the only platform that delivers FedNow, RTP, Stablecoin, and Tokenized Deposits through a single bank-branded wallet — FI-channel, bank-owned, and examiner-ready.
            </p>

            <div className="flex flex-col gap-6">
              {differentiators.map((d, i) => {
                const Icon = d.icon;
                return (
                  <div
                    key={d.title}
                    className="flex gap-4"
                    style={{
                      opacity: revealed ? 1 : 0,
                      transform: revealed ? 'translateX(0)' : 'translateX(-20px)',
                      transition: `all 0.6s ease ${0.3 + i * 0.15}s`,
                    }}
                  >
                    <div className="flex-shrink-0 mt-1 p-2 border border-[#00FF00] border-opacity-30"
                      style={{ background: 'rgba(0,255,0,0.05)' }}>
                      <Icon size={18} color="#00FF00" />
                    </div>
                    <div>
                      <h4 className="text-white font-bold text-sm mb-1 uppercase tracking-wide"
                        style={{ fontFamily: 'Unbounded, sans-serif', fontSize: '12px' }}>
                        {d.title}
                      </h4>
                      <p className="text-[#A3A3A3] text-sm leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {d.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              {['White-Label', 'Four-Rail', 'Smart Router', 'Examiner-Ready', 'BSA/AML Built-In', 'GENIUS Act Ready'].map((tag) => (
                <span
                  key={tag}
                  className="flex items-center gap-1.5 border border-[#00FF00] border-opacity-30 px-3 py-1.5 text-xs font-mono text-[#00FF00] uppercase tracking-wider"
                  style={{ background: 'rgba(0,255,0,0.05)' }}
                >
                  <Check size={10} /> {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Right: Visual */}
          <div
            style={{
              opacity: revealed ? 1 : 0,
              transform: revealed ? 'translateX(0)' : 'translateX(40px)',
              transition: 'all 0.8s ease 0.2s',
            }}
          >
            <div
              className="relative p-8 border border-[#1A1A1A]"
              style={{
                background: '#0A0A0A',
                boxShadow: '0 0 60px rgba(0,255,0,0.08), inset 0 0 60px rgba(0,255,0,0.02)',
              }}
            >
              {/* Corner accents */}
              {['top-0 left-0', 'top-0 right-0', 'bottom-0 left-0', 'bottom-0 right-0'].map((pos) => (
                <div
                  key={pos}
                  className={`absolute ${pos} w-4 h-4 border-[#00FF00]`}
                  style={{
                    borderTopWidth: pos.includes('top') ? '2px' : 0,
                    borderBottomWidth: pos.includes('bottom') ? '2px' : 0,
                    borderLeftWidth: pos.includes('left') ? '2px' : 0,
                    borderRightWidth: pos.includes('right') ? '2px' : 0,
                  }}
                />
              ))}

              {/* Logo */}
              <div className="flex justify-center mb-8">
                <div className="relative">
                  <div className="absolute inset-0 opacity-30"
                    style={{ background: 'radial-gradient(circle, rgba(0,255,0,0.4), transparent 70%)', filter: 'blur(20px)' }} />
                  <img
                    src={SPLASH_URL}
                    alt=""
                    className="relative w-full max-w-xs mx-auto opacity-60"
                    style={{ filter: 'hue-rotate(0deg)' }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <img src={LOGO_URL} alt="NexGenPay" className="h-16 w-auto" />
                  </div>
                </div>
              </div>

              {/* Comparison */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 border border-[#EF4444] border-opacity-30"
                  style={{ background: 'rgba(239,68,68,0.05)' }}>
                  <div className="text-[#EF4444] text-xs font-mono uppercase tracking-widest mb-3">Without NexGenPay</div>
                  {['$0 FedNow transaction revenue', 'Two+ separate vendor contracts', 'No stablecoin or tokenized deposits', 'FedNow receive-only mode'].map((item) => (
                    <div key={item} className="flex items-center gap-2 mb-2">
                      <div className="w-1 h-1 bg-[#EF4444] rounded-full flex-shrink-0" />
                      <span className="text-[#A3A3A3] text-xs" style={{ fontFamily: 'Manrope, sans-serif' }}>{item}</span>
                    </div>
                  ))}
                </div>
                <div className="p-4 border border-[#00FF00] border-opacity-40"
                  style={{ background: 'rgba(0,255,0,0.05)', boxShadow: '0 0 20px rgba(0,255,0,0.05)' }}>
                  <div className="text-[#00FF00] text-xs font-mono uppercase tracking-widest mb-3">With NexGenPay</div>
                  {['All four rails, one integration', 'Single vendor, full-stack', 'Stablecoin + 4.25% APY vault', 'Full FedNow send + receive'].map((item) => (
                    <div key={item} className="flex items-center gap-2 mb-2">
                      <Check size={10} className="text-[#00FF00] flex-shrink-0" />
                      <span className="text-white text-xs" style={{ fontFamily: 'Manrope, sans-serif' }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
